<script setup>
import { computed, ref, defineProps, watch } from 'vue'
import PlaceInformation from '@/components/object_placeCollection/templates/placeInformation.vue'
import { formatDate, formatDateRange, getDayName } from '@/mixins/global/globalPlaces'

const props = defineProps({
    placeInformation: {
        type: Object,
        default: null
    }
})

const activeWeekIndex = ref(0)

const weeks = computed(() => {
    if (!props.placeInformation) return []

    const dates = Object.keys(props.placeInformation)

    const result = []
    for (let i = 0; i < dates.length; i += 7) {
        const weekDates = dates.slice(i, i + 7)
        if (weekDates.length) {
            result.push(weekDates)
        }
    }
    return result
})

const activeDates = computed(() => {
    if (!weeks.value.length) return []

    const safeIndex = Math.min(activeWeekIndex.value, weeks.value.length - 1)
    return weeks.value[safeIndex]
})

watch(
    () => weeks.value.length,
    (newLength) => {
        if (newLength > 0 && activeWeekIndex.value >= newLength) {
            activeWeekIndex.value = newLength - 1
        }
    }
)

const firstDay = computed(() => {
    if (activeDates.value.length === 0) return null
    return activeDates.value[0]
})

const lastDay = computed(() => {
    if (activeDates.value.length === 0) return null
    return activeDates.value[activeDates.value.length - 1]
})

const weekNumber = computed(() => {
    if (!firstDay.value) return ''

    const date = new Date(firstDay.value)
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1)
    const pastDaysOfYear = Math.floor((date - firstDayOfYear) / 86400000)
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7)
})

const uniquePlaces = computed(() => {
    if (!props.placeInformation || !activeDates.value.length) return []

    const places = new Map()

    for (const dateKey of activeDates.value) {
        const dayData = props.placeInformation[dateKey]
        if (dayData?.places) {
            for (const place of dayData.places) {
                if (!places.has(place.id)) {
                    places.set(place.id, place)
                }
            }
        }
    }

    return Array.from(places.values())
})

const hasPreviousWeek = computed(() => {
    return activeWeekIndex.value > 0
})

const hasNextWeek = computed(() => {
    return activeWeekIndex.value < weeks.value.length - 1
})

function previousWeek() {
    if (hasPreviousWeek.value) {
        activeWeekIndex.value--
    }
}

function nextWeek() {
    if (hasNextWeek.value) {
        activeWeekIndex.value++
    }
}

function hasPlaceForDate(dateKey, place) {
    if (!props.placeInformation[dateKey] || !props.placeInformation[dateKey].places) {
        return false
    }

    return props.placeInformation[dateKey].places.some(p => p.id === place.id)
}

function getPlaceForDate(dateKey, place) {
    if (!hasPlaceForDate(dateKey, place)) return null

    return props.placeInformation[dateKey].places.find(p => p.id === place.id)
}
</script>

<template>
    <div class="week-table-container">
        <div v-if="!placeInformation || Object.keys(placeInformation).length === 0" class="alert alert-info">
            Keine Platzinformationen verfügbar.
        </div>

        <div v-else>
            <div class="week-navigation">
                <button
                    @click="previousWeek"
                    class="week-button"
                    :disabled="!hasPreviousWeek"
                    aria-label="Vorherige Woche"
                >
                    <span class="week-icon">&#10094;</span>
                </button>

                <div class="week-header">
                    <template v-if="firstDay && new Date(firstDay).getDay() === 1">
                        <span class="calendar-week">KW {{ weekNumber }} (</span>{{
                            formatDateRange(firstDay, lastDay)
                        }}<span class="calendar-week">)</span>
                    </template>
                    <template v-else>
                        {{ formatDateRange(firstDay, lastDay) }}
                    </template>
                </div>

                <button
                    @click="nextWeek"
                    class="week-button"
                    :disabled="!hasNextWeek"
                    aria-label="Nächste Woche"
                >
                    <span class="week-icon">&#10095;</span>
                </button>
            </div>

            <div class="responsive-table-wrapper">
                <table class="week-table">
                    <thead>
                    <tr>
                        <th class="place-name-header">Platz</th>
                        <th v-for="dateKey in activeDates" :key="dateKey" class="day-header">
                            {{ getDayName(dateKey) }}<br>
                            <span class="day-date">{{ formatDate(dateKey) }}</span>
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="place in uniquePlaces" :key="place.id">
                        <td class="place-name">{{ place.name }}</td>
                        <td
                            v-for="dateKey in activeDates"
                            :key="`${place.id}-${dateKey}`"
                            :class="[
                                'day-cell',
                                hasPlaceForDate(dateKey, place)
                                ? (getPlaceForDate(dateKey, place).open ? 'open' : 'closed')
                                : 'no-data'
                            ]"
                        >
                            <place-information
                                v-if="hasPlaceForDate(dateKey, place)"
                                :place="getPlaceForDate(dateKey, place)"
                            />
                            <span v-else class="no-info">-</span>
                        </td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.week-navigation {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;

    .week-button {
        background: none;
        border: 1px solid #eeeeee;
        width: 2.25em;
        height: 2.25em;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        transition: all 0.25s ease;

        &:hover:not(:disabled) {
            border-color: #b7b7b7;
        }

        &:disabled {
            opacity: 0;
            cursor: unset;
        }
    }

    .week-header {
        text-align: center;

        p {
            font-weight: bold;
            margin: 0;
        }
    }
}

.responsive-table-wrapper {
    overflow-x: auto;
    max-width: 100%;
    width: 100%;
}

.week-table {
    width: 100%;
    min-width: 60em;
    border-collapse: collapse;

    th, td {
        padding: 0.5rem;
        border: 1px solid #eeeeee;
        font-size: 0.875rem;
    }

    th {
        background-color: #fafafa;
        font-weight: bold;
    }

    td {
        text-align: left;
        background-color: #ffffff;
    }

    .place-name-header {
        text-align: left;
    }

    .place-info {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }
}
</style>
